import { Box, Button, Heading, Text, VStack } from 'native-base';
import React, { ReactNode, useEffect, useState } from 'react';

import CPActionSheet from 'app/components/CPActionSheet/CPActionSheet';
import CPAlert from 'app/components/CPAlert';
import HTML from 'app/components/HTML';
import ReservationCancel from 'app/components/ReservationCancel';
import { AttractionContent } from 'app/hooks/useAttraction';
import useAttractionOffline from 'app/hooks/useAttractionOffline';
import useReservationTextTranslationHelper from 'app/hooks/useReservationTextTranslationHelper';
import { EntitlementReservation } from 'app/services/GuestCenterService.types';
import { canCancel } from 'app/services/ReservationHelper';
import { tID } from 'app/services/TestHelper';

export type CancelReservationActionSheetProps = {
  children?: ReactNode;
  reservation: EntitlementReservation;
  attractionContent: AttractionContent;
  isOpen: boolean;
  onClose: () => void;
  onModify: () => void;
};

export default function ReservationModifyCancelSheet({
  reservation,
  attractionContent,
  onClose,
  isOpen,
  onModify,
}: CancelReservationActionSheetProps) {
  const rt = useReservationTextTranslationHelper({ attraction: attractionContent });
  const [showCancel, setShowCancel] = useState(false);
  const isAttractionOffline = useAttractionOffline({ attractionKey: reservation.attractionKey });
  const isChangeNotAllowed = !canCancel(
    reservation.attractionConfig,
    reservation.reservationDateTime
  );
  const shouldDisableBtns = isAttractionOffline || isChangeNotAllowed;

  useEffect(() => {
    if (!isOpen) setTimeout(() => setShowCancel(false), 500);
  }, [isOpen]);

  return (
    <CPActionSheet
      closeOnOverlayPress={false}
      isOpen={isOpen}
      showHeader={false}
      testID={tID('CancelReservationActionSheet')}
      onClose={onClose}
    >
      <Box p="4">
        {showCancel ? (
          <ReservationCancel reservation={reservation} onAbort={onClose} onSuccess={onClose} />
        ) : (
          <VStack space="4">
            <Heading>{rt('reservation_action_title')}</Heading>

            {isAttractionOffline && (
              <CPAlert status="warning">
                <HTML
                  html={rt('offline_cancel_content', {
                    attraction_name: attractionContent?.name ?? '',
                  })}
                />
              </CPAlert>
            )}

            {isChangeNotAllowed && (
              <CPAlert status="error" title={rt('reservation_contact_customer_service')}>
                <Text>{rt('reservation_cancel_assistance')}</Text>
              </CPAlert>
            )}

            <Button.Group w="full">
              <Button isDisabled={shouldDisableBtns} w="full" onPress={onModify}>
                {rt('reservation_action_modify')}
              </Button>
              <Button
                isDisabled={shouldDisableBtns}
                variant="outline"
                w="full"
                onPress={() => setShowCancel(true)}
              >
                {rt('reservation_action_cancel')}
              </Button>
              <Button variant="link" onPress={onClose}>
                {rt('reservation_action_abort')}
              </Button>
            </Button.Group>
          </VStack>
        )}
      </Box>
    </CPActionSheet>
  );
}
